"use client";

import { useMemo, useState } from "react";
import type { SVGProps } from "react";
import { createPortal } from "react-dom";
import type { FunnelPlannerContent } from "@/lib/content/entries/funnel-planner";
import { funnelText, stageTypeLabel } from "@/lib/funnel-planner/i18n";
import { getStageTypesByGroup, type StageTypeMeta } from "@/lib/funnel-planner/stageTypes";

type IconProps = SVGProps<SVGSVGElement>;
type StageIconComponent = (p: IconProps) => React.JSX.Element;

function IconBase(p: IconProps) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...p}>
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <path d="M8 9h8M8 13h6M8 17h4" />
    </svg>
  );
}

function IconAcquisition(p: IconProps) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...p}>
      <path d="M3 12h18M12 3v18" />
      <circle cx="12" cy="12" r="8" />
    </svg>
  );
}

function IconNurture(p: IconProps) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...p}>
      <path d="M4 4h16v16H4z" />
      <path d="m4 7 8 6 8-6" />
    </svg>
  );
}

function IconConversion(p: IconProps) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...p}>
      <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  );
}

function IconUtility(p: IconProps) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...p}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1-2.9 2.9-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V22h-4v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1-2.9-2.9.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H2v-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.8l-.1-.1 2.9-2.9.1.1a1.7 1.7 0 0 0 1.8.3 1.7 1.7 0 0 0 1-1.5V2h4v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1 2.9 2.9-.1.1a1.7 1.7 0 0 0-.3 1.8 1.7 1.7 0 0 0 1.5 1h.1v4h-.1a1.7 1.7 0 0 0-1.5 1z" />
    </svg>
  );
}

const ICONS_BY_GROUP: Record<StageTypeMeta["group"], StageIconComponent> = {
  acquisition: IconAcquisition,
  nurture: IconNurture,
  conversion: IconConversion,
  retention: IconBase,
  plg: IconBase,
  utility: IconUtility,
};

export const DEFAULT_RECOMMENDED_STAGE_TYPES = [
  "landing-page",
  "opt-in-page",
  "email-sequence",
  "sales-page",
  "order-form",
  "booking-page",
  "discovery-call",
  "thank-you-page",
] as const;

type Props = {
  value: string;
  onChange: (type: string) => void;
  onClose: () => void;
  content: FunnelPlannerContent;
  compact?: boolean;
  recommendedTypes?: readonly string[];
};

export function getStageTypeMeta(type: string) {
  const meta = Object.values(getStageTypesByGroup()).flat().find((item) => item.id === type);
  if (!meta) return { label: type, Icon: IconUtility };
  return { label: meta.label, Icon: ICONS_BY_GROUP[meta.group] ?? IconBase };
}

export function StageTypePicker({
  value,
  onChange,
  onClose,
  content,
  compact,
  recommendedTypes = DEFAULT_RECOMMENDED_STAGE_TYPES,
}: Props) {
  const [query, setQuery] = useState("");
  const groups = useMemo(() => getStageTypesByGroup(), []);
  const normalizedQuery = query.trim().toLowerCase();
  const allTypes = useMemo(() => Object.values(groups).flat(), [groups]);
  const recommended = useMemo(() => {
    const candidates = recommendedTypes
      .map((id) => allTypes.find((item) => item.id === id))
      .filter((item): item is StageTypeMeta => Boolean(item));
    if (!normalizedQuery) return candidates;
    return candidates.filter((item) => {
      const label = stageTypeLabel(content, item.id, item.label);
      return `${label} ${item.description} ${item.group}`.toLowerCase().includes(normalizedQuery);
    });
  }, [allTypes, content, normalizedQuery, recommendedTypes]);
  const recommendedIds = useMemo(() => new Set(recommended.map((item) => item.id)), [recommended]);

  const picker = (
    <div
      className="nodrag nopan"
      role="listbox"
      aria-label={funnelText(content, "canvasChooseStageTypeLabel", "Choose stage type")}
      data-funnel-canvas-stage-picker={compact ? "compact" : "default"}
      style={compact
        ? {
            position: "fixed",
            zIndex: 10000,
            left: 8,
            right: 8,
            bottom: 8,
            width: "auto",
            maxWidth: "none",
            maxHeight: "min(68vh, 31rem)",
            overflowY: "auto",
            borderRadius: 10,
            border: "1px solid #334155",
            background: "#1e293b",
            boxShadow: "0 18px 44px rgba(0,0,0,0.58)",
            padding: 10,
          }
        : { position: "absolute", zIndex: 20, top: "calc(100% + 4px)", insetInlineStart: 0, width: 340, maxWidth: "min(90vw, 340px)", borderRadius: 8, border: "1px solid #1e293b", background: "#1e293b", boxShadow: "0 8px 24px rgba(0,0,0,0.4)", padding: 10 }}
      onMouseDown={e => e.stopPropagation()}
      onClick={e => e.stopPropagation()}
      onContextMenu={e => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <input
        dir="auto"
        type="search"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder={funnelText(content, "canvasStageSearchPlaceholder", "Search stages")}
        aria-label={funnelText(content, "canvasStageSearchAria", "Search funnel stage types")}
        className="nodrag nopan"
        style={{ width: "100%", borderRadius: 6, border: "1px solid #334155", background: "#0f172a", color: "#e2e8f0", padding: "7px 9px", fontSize: 12, marginBottom: 8, boxSizing: "border-box", textAlign: "start" }}
      />
      <div style={{ maxHeight: compact ? "calc(min(68vh, 31rem) - 4rem)" : 380, overflowY: "auto", paddingRight: 2 }}>
        {recommended.length > 0 && (
          <section style={{ marginBottom: 10 }}>
            <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0, margin: "4px 2px 5px" }}>
              {funnelText(content, "canvasRecommendedStageTypesLabel", "Recommended")}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: compact ? "repeat(auto-fit, minmax(8.5rem, 1fr))" : "1fr 1fr", gap: 4 }}>
              {recommended.map((item) => {
                const isSelected = value === item.id;
                const label = stageTypeLabel(content, item.id, item.label);
                const Icon = ICONS_BY_GROUP[item.group] ?? IconBase;
                return (
                  <button
                    key={item.id}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    title={item.description}
                    onClick={() => { onChange(item.id); onClose(); }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      borderRadius: 6,
                      padding: "7px 8px",
                      fontSize: 12,
                      textAlign: "start",
                      cursor: "pointer",
                      border: "1px solid transparent",
                      background: isSelected ? "rgba(37,99,235,0.16)" : "transparent",
                      color: isSelected ? "#93c5fd" : "#e2e8f0",
                      fontWeight: isSelected ? 650 : 450,
                      width: "100%",
                    }}
                  >
                    <Icon />
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
                  </button>
                );
              })}
            </div>
          </section>
        )}
        {Object.entries(groups).map(([group, items]) => {
          const matches = items.filter((item) => {
            if (recommendedIds.has(item.id)) return false;
            const label = stageTypeLabel(content, item.id, item.label);
            return !normalizedQuery || `${label} ${item.description} ${item.group}`.toLowerCase().includes(normalizedQuery);
          });
          if (matches.length === 0) return null;
          return (
            <section key={group} style={{ marginBottom: 10 }}>
              <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0, margin: "4px 2px 5px" }}>
                {group.replace(/-/g, " ")}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: compact ? "repeat(auto-fit, minmax(8.5rem, 1fr))" : "1fr 1fr", gap: 4 }}>
                {matches.map((item) => {
                  const isSelected = value === item.id;
                  const label = stageTypeLabel(content, item.id, item.label);
                  const Icon = ICONS_BY_GROUP[item.group] ?? IconBase;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      title={item.description}
                      onClick={() => { onChange(item.id); onClose(); }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        borderRadius: 6,
                        padding: "7px 8px",
                        fontSize: 12,
                        textAlign: "start",
                        cursor: "pointer",
                        border: "1px solid transparent",
                        background: isSelected ? "rgba(37,99,235,0.16)" : "transparent",
                        color: isSelected ? "#93c5fd" : "#e2e8f0",
                        fontWeight: isSelected ? 650 : 450,
                        width: "100%",
                      }}
                    >
                      <Icon />
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
                    </button>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );

  if (compact && typeof document !== "undefined") {
    return createPortal(picker, document.body);
  }

  return picker;
}
